import React, { useState } from 'react';
import { useEscapeKey } from '../../../hooks/useEscapeKey';
import { useTranslation } from 'react-i18next';
import { INSERTABLE_NODE_TYPES, typeLabelOf, categoryLabelOf, insertBlockedReason, isOnHoldType } from '../nodeCatalog';

/** 합류 노드 선택값 — 지정 안 함 / 새로 만들기 / 캔버스에 있는 합류 노드의 id */
export const MERGE_NONE = '';
export const MERGE_NEW = '__new__';

export interface BranchAddResult { type: string; count: number; merge: string }

interface Props {
  /** 분기 노드 이름 — 어느 분기에 더하는지 제목에 보여 준다 */
  branchLabel: string;
  /** 캔버스에 이미 있는 합류 노드들 */
  merges: Array<{ id: string; label: string }>;
  onAdd: (r: BranchAddResult) => void;
  onClose: () => void;
}

/** 한 번에 더할 수 있는 후행 개수 — 이보다 많으면 캔버스에서 읽을 수 없다 */
const MAX_COUNT = 10;

/**
 * 분기 노드의 + 를 누르면 뜨는 창 — 후행 노드의 **종류·개수·합류 지점**을 한 번에 정한다.
 *
 * 일반 노드의 + 는 유형만 고르면 됐지만 분기는 갈래를 여는 일이라, 몇 갈래인지와 어디서 다시 만나는지가
 * 같이 정해져야 한 번에 그림이 된다. 나중에 하나씩 더해도 같은 규칙으로 아래에 쌓인다.
 */
const BranchAddDialog: React.FC<Props> = ({ branchLabel, merges, onAdd, onClose }) => {
  const { t } = useTranslation();
  useEscapeKey(onClose);
  const [type, setType] = useState('');
  const [count, setCount] = useState(2);
  const [merge, setMerge] = useState<string>(MERGE_NONE);

  const add = () => { if (type) onAdd({ type, count, merge }); };
  const sel = 'w-full border border-gray-300 rounded px-2 py-1 text-sm bg-white';

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-30" onMouseDown={onClose}>
      <div className="bg-white rounded shadow-lg w-[420px] max-w-[95vw]" onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <div>
            <div className="text-sm font-semibold text-gray-800">{t('workflow:branchAdd.title')}</div>
            <div className="text-xs text-gray-500">{t('workflow:branchAdd.subtitle', { name: branchLabel })}</div>
          </div>
          <button className="px-2 py-1 text-xs rounded hover:bg-gray-100 text-gray-600" onClick={onClose} title={t('common:ui.close')}>✕</button>
        </div>

        <div className="p-4 space-y-3">
          <label className="block">
            <span className="block text-xs font-semibold text-gray-500 mb-1">{t('workflow:branchAdd.type')}</span>
            <select className={sel} value={type} onChange={(e) => setType(e.target.value)} autoFocus>
              <option value="">{t('workflow:branchAdd.chooseType')}</option>
              {(['data', 'process', 'control', 'exec'] as const).map((cat) => (
                <optgroup key={cat} label={categoryLabelOf(cat)}>
                  {INSERTABLE_NODE_TYPES.filter((e) => e.category === cat).map((e) => {
                    // 분기 뒤에 올 수 없는 유형은 고를 수 없다 (캔버스의 + 메뉴와 같은 규칙)
                    const blocked = insertBlockedReason(e.type, ['branch']);
                    const hold = !blocked && isOnHoldType(e.type) ? ` — ${t('workflow:toolbox.onHold')}` : '';
                    return <option key={e.type} value={e.type} disabled={!!blocked}>{typeLabelOf(e.type)}{blocked ? ` — ${t('workflow:insert.blocked')}` : hold}</option>;
                  })}
                </optgroup>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="block text-xs font-semibold text-gray-500 mb-1">{t('workflow:branchAdd.count')}</span>
            <input type="number" min={1} max={MAX_COUNT} className={sel} value={count}
              onChange={(e) => setCount(Math.max(1, Math.min(MAX_COUNT, Number(e.target.value) || 1)))} />
            <span className="block text-[0.6875rem] text-gray-500 mt-1">{t('workflow:branchAdd.countHint', { max: MAX_COUNT })}</span>
          </label>

          <label className="block">
            <span className="block text-xs font-semibold text-gray-500 mb-1">{t('workflow:branchAdd.merge')}</span>
            <select className={sel} value={merge} onChange={(e) => setMerge(e.target.value)}>
              <option value={MERGE_NONE}>{t('workflow:branchAdd.mergeNone')}</option>
              <option value={MERGE_NEW}>{t('workflow:branchAdd.mergeNew')}</option>
              {merges.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
            <span className="block text-[0.6875rem] text-gray-500 mt-1">{t('workflow:branchAdd.mergeHint')}</span>
          </label>
        </div>

        <div className="px-4 py-2 border-t border-gray-200 flex justify-end gap-2">
          <button className="px-3 py-1 text-xs rounded bg-gray-200 hover:bg-gray-300 text-gray-700" onClick={onClose}>{t('common:ui.cancel')}</button>
          <button className="px-3 py-1 text-xs rounded bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50" disabled={!type} onClick={add}>{t('common:ui.add')}</button>
        </div>
      </div>
    </div>
  );
};

export default BranchAddDialog;
